// Client-side prep for note-request photos. Every upload leaves here at
// ≤ 900 KB so it fits under nginx's default 1 MB body limit, and JPEG/WebP
// are always re-encoded so phone EXIF (GPS) never leaves the device.
//
// The CSP (img-src 'self' data:) blocks blob: URLs, so URL.createObjectURL is
// never used: decoding goes through createImageBitmap (not CSP-gated) and the
// preview is a FileReader data URL.

export const MAX_ATTACHMENTS = 5;

const TARGET_BYTES = 900 * 1024;
const MAX_EDGE = 2400;
const MIN_EDGE = 1200;
const JPEG_QUALITIES = [0.9, 0.8, 0.7, 0.6];
const DECODE_ERROR = 'Use a PNG or JPG screenshot';
const IMAGE_EXT = /\.(png|jpe?g|webp|gif|bmp|heic|heif|avif)$/i;

export function isImageFile(f) {
  if (!f) return false;
  if (f.type) return f.type.startsWith('image/');
  return IMAGE_EXT.test(f.name || '');
}

// Image files from a paste (ClipboardEvent.clipboardData) or a drop
// (DragEvent.dataTransfer). Clipboards expose the same screenshot through
// both .items and .files, so .files is only the fallback.
export function imageFilesFrom(dt) {
  if (!dt) return [];
  const out = [];
  for (const item of Array.from(dt.items || [])) {
    if (item.kind !== 'file') continue;
    const f = item.getAsFile();
    if (isImageFile(f)) out.push(f);
  }
  if (out.length) return out;
  return Array.from(dt.files || []).filter(isImageFile);
}

export function formatBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function baseName(name) {
  const n = String(name || '').replace(/^.*[\\/]/, '');
  const dot = n.lastIndexOf('.');
  return (dot > 0 ? n.slice(0, dot) : n).trim() || 'photo';
}

function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error(DECODE_ERROR));
    r.readAsDataURL(blob);
  });
}

function toBlob(canvas, type, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

async function decode(file) {
  if (typeof createImageBitmap !== 'function') throw new Error(DECODE_ERROR);
  try {
    // Phone photos carry their rotation in EXIF; apply it before it's stripped
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    try { return await createImageBitmap(file); } catch { throw new Error(DECODE_ERROR); }
  }
}

// Long edge capped at `edge`; JPEG gets a white background (no alpha)
function draw(bitmap, edge, whiteBg) {
  const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext('2d');
  if (whiteBg) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return canvas;
}

async function encode(bitmap, isPng) {
  let edge = Math.min(MAX_EDGE, Math.max(bitmap.width, bitmap.height));
  let triedPng = !isPng;
  for (;;) {
    // Screenshots stay PNG when they fit — text stays crisp
    if (!triedPng) {
      triedPng = true;
      const png = await toBlob(draw(bitmap, edge, false), 'image/png');
      if (png && png.size <= TARGET_BYTES) return { blob: png, ext: 'png' };
    }
    const canvas = draw(bitmap, edge, true);
    for (const q of JPEG_QUALITIES) {
      const jpg = await toBlob(canvas, 'image/jpeg', q);
      if (jpg && jpg.size <= TARGET_BYTES) return { blob: jpg, ext: 'jpg' };
    }
    if (edge <= MIN_EDGE) break;
    edge = Math.max(MIN_EDGE, Math.round(edge * 0.75));
  }
  throw new Error('Image is too large even after compressing');
}

// File → { blob, name, previewDataUrl }. Throws an Error whose message is
// fit to show the user.
export async function prepareImage(file) {
  if (!file || (file.type && !file.type.startsWith('image/'))) throw new Error(DECODE_ERROR);
  const base = baseName(file.name);
  if (file.type === 'image/png' && file.size > 0 && file.size <= TARGET_BYTES) {
    return { blob: file, name: `${base}.png`, previewDataUrl: await readAsDataUrl(file) };
  }
  const bitmap = await decode(file);
  try {
    const { blob, ext } = await encode(bitmap, file.type === 'image/png');
    return { blob, name: `${base}.${ext}`, previewDataUrl: await readAsDataUrl(blob) };
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
}
